import {
  BulletedListIcon,
  DeleteIcon,
  Divider,
  GKButton,
  Grid2,
  KebabMenuIcon,
  PlusIcon,
  Popover,
  StatusField,
  Typography,
  WhatsappIcon,
} from "components";
import { useMutation } from "hooks/useQueries";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { matchDate, refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import UpdateLead from "../UpdatetLead";
import LeadAssignPopup from "./LeadAssignPopup";
import LeadStatusPopup from "./LeadStatusPopup";
import LeadTagsPopup from "./LeadTagsPopup";
import SendMarketingMaterial from "./SendMarketingMaterial";

interface Props {
  leadData: {
    id: number;
    leadStatusStr: string;
    leadAssignedToStr: string;
    sourceOfLeadStr: string;
    tagsList: any[];
    hasMeeting: boolean;
    hasNotes: boolean;
    meetingCount: number;
    notesCount: number;
    name: string;
    email: string;
    companyName: string;
    countryCode: string;
    phoneNumber: string;
    lastContacted: string;
    riskStatus: string;
    panNumber: string;
    position: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
    leadValue: string;
    description: string;
    conversionStatus: false;
    createdAt: string;
    updatedAt: string;
    branch: number;
    leadStatus: number;
    leadAssignedTo: number;
    sourceOfLead: number;
    tags: any[];
  };
}

const LeadCard = (props: Props) => {
  const { leadData } = props;

  const { id } = useParams();

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [updateModal, setUpdateModal] = useState(false);

  const [sendMaterialModal, setSendMaterialModal] = useState(false);

  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: convertToCustomer } = useMutation({
    mutationKey: ["CONVERT_TO_CUSTOMER"],
    mutationFn: ({ id }: { id: any }) =>
      axiosInstance.post(`/client/${id}/convert/`),
    onSuccess: () => {
      refetchQuery(QueryKeys.GET_LEAD);
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: handleSendRisk, isLoading: riskLoading } = useMutation({
    mutationFn: (da: any) =>
      axiosInstance.post(`/lead-questionnaire-request/`, {
        lead_id: da,
      }),
    onSuccess: () => {
      refetchQuery(QueryKeys.GET_LEAD);
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: handledDeleteLead, isLoading: deleteLoading } = useMutation({
    mutationFn: (da: any) => axiosInstance.delete(`/lead/${da}`),
    mutationKey: ["DELETE_LEAD"],
    onSuccess: () => {
      refetchQuery(QueryKeys.GET_LEAD);
      navigate(-1);
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###

  return (
    <>
      <Grid2 container spacing={1}>
        <Grid2 size={8} container spacing={1} alignItems={"center"}>
          <Grid2 size={12} container spacing={1}>
            <Grid2>
              <Typography fontWeight={600} fontSize={24}>
                {leadData?.name || "*Update Name"}
              </Typography>
              <Typography
                fontWeight={400}
                fontSize={12}
                color={"var(--neutral-500)"}
              >
                {leadData?.companyName || "*Update Company Name"}
              </Typography>
            </Grid2>
            <Grid2>
              <LeadStatusPopup leadData={leadData} />
            </Grid2>
          </Grid2>
        </Grid2>
        <Grid2 size={4} container spacing={1} alignItems={"center"}>
          <Grid2 size={12} display={"flex"} justifyContent={"flex-end"} gap={1}>
            <GKButton
              className="convert-to-client"
              variant="contained"
              size="medium"
              onClick={() => convertToCustomer({ id: id })}
            >
              Convert to client
            </GKButton>
            <GKButton
              color="dark"
              variant="outlined"
              startIcon={
                <WhatsappIcon
                  width={16}
                  height={16}
                  color={"var(--neutral-500)"}
                />
              }
              onClick={() => {
                setSendMaterialModal(true);
              }}
            >
              Send Marketing
            </GKButton>
            <GKButton
              variant="outlined"
              color="dark"
              style={{
                minWidth: 1,
                width: 40,
              }}
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
              }}
            >
              <KebabMenuIcon />
            </GKButton>
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <Divider />
        </Grid2>
        <Grid2 size={12} display={"flex"} alignItems={"center"} gap={3}>
          <div>
            <LeadAssignPopup leadData={leadData} />
          </div>
          <div>
            <LeadTagsPopup leadData={leadData} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography color={"var(--neutral-500)"} fontSize={12}>
              Risk Profile
            </Typography>
            <StatusField statusString={leadData?.riskStatus || "pending"} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography color={"var(--neutral-500)"} fontSize={12}>
              Last Contacted
            </Typography>
            <Typography>{matchDate(leadData?.lastContacted)}</Typography>
          </div>
        </Grid2>
      </Grid2>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        PaperProps={{
          style: { width: "15%", borderRadius: 16 },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Grid2 container sx={{ py: "6px" }}>
          <Grid2 size={12}>
            <GKButton
              startIcon={
                <PlusIcon width={16} height={16} color={"var(--neutral-500)"} />
              }
              color="dark"
              fullWidth
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0,
                justifyContent: "flex-start",
              }}
              onClick={() => {
                navigate(`/app/lead/${id}/questionnaire`);
              }}
            >
              Take Quiz{" "}
            </GKButton>
          </Grid2>
          <Grid2 size={12}>
            <GKButton
              loading={riskLoading}
              startIcon={
                <BulletedListIcon
                  height={16}
                  width={16}
                  color={"var(--neutral-500)"}
                />
              }
              color="dark"
              fullWidth
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0,
                justifyContent: "flex-start",
              }}
              onClick={() => {
                handleSendRisk(id);
              }}
            >
              Request Questionnaire{" "}
            </GKButton>
          </Grid2>
          <Grid2 size={12}>
            <GKButton
              onClick={() => {
                handledDeleteLead(id);
              }}
              loading={deleteLoading}
              startIcon={<DeleteIcon width={16} color={"var(--neutral-500)"} />}
              color="dark"
              fullWidth
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0,
                justifyContent: "flex-start",
              }}
            >
              Delete
            </GKButton>
          </Grid2>
        </Grid2>
      </Popover>

      {updateModal && (
        <UpdateLead setOpen={setUpdateModal} open={updateModal} id={id} />
      )}

      {sendMaterialModal && (
        <SendMarketingMaterial
          setOpen={setSendMaterialModal}
          open={sendMaterialModal}
          id={id}
        />
      )}
    </>
  );
};

export default LeadCard;
